'use client';

import { TrafficEventType } from '@prisma/client';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';

// ❌ УДАЛИ ЭТОТ ИМПОРТ: import { createTrafficReport } from '../lib/actions/trafficActions';

type ReportJamDialogProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  coordinates: number[] | null;
  onReported: () => void;
  district?: string | null;
};

export function ReportJamDialog({
  isOpen,
  setIsOpen,
  coordinates,
  onReported,
  district,
}: ReportJamDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!coordinates) return;

    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    formData.append('latitude', String(coordinates[0]));
    formData.append('longitude', String(coordinates[1]));
    if (district) {
      formData.append('district', district);
    }

    try {
      // ✅ ЗАМЕНА ЭКШЕНА НА API ROUTE
      const response = await fetch('/api/traffic/report/create', {
        method: 'POST',
        body: formData, // fetch сам выставит правильный Content-Type для FormData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Ошибка при создании');
      }

      toast.success(result.message || 'Спасибо! Сообщение добавлено.');
      onReported();
      setIsOpen(false);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Не удалось отправить сообщение');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ... (rest of the component remains the same: handleFileChange, render)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size > 3 * 1024 * 1024) {
      toast.error('Фото слишком большое! Максимальный размер - 3 МБ.');
      e.target.value = '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px] bg-gray-900 border-gray-700 text-white">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Сообщить о пробке</DialogTitle>
            <DialogDescription>
              Выберите точку на карте, заполните детали и помогите другим водителям.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* ... Твои инпуты (без изменений) ... */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Ти
              </Label>
              <Select name="type" required>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Выберите причину" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TrafficEventType.CONGESTION}>Затор</SelectItem>
                  <SelectItem value={TrafficEventType.ACCIDENT}>Авария</SelectItem>
                  <SelectItem value={TrafficEventType.ROAD_WORKS}>Ремонт</SelectItem>
                  <SelectItem value={TrafficEventType.OTHER}>Другое</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="comment" className="text-right">
                Комментарий
              </Label>
              <Textarea id="comment" name="comment" className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="photo" className="text-right">
                Фото
              </Label>
              <Input
                id="photo"
                name="photo"
                type="file"
                className="col-span-3"
                onChange={handleFileChange}
                accept="image/*"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting || !coordinates}>
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
